/*
  ga-creator-attribution.js — persistent creator-sale attribution (2026-09-22).

  Tracks first-touch/last-touch creator-page visits in first-party
  localStorage (grandma-anns.com only, versioned key ga_creator_attribution_v1,
  180-day fixed expiry from first touch) and syncs a small set of readable
  cart attributes via Shopify's supported Ajax Cart API (/cart.js + POST
  /cart/update.js with { attributes }). Detailed UTM data is written as
  separate, underscore-prefixed "internal" attributes, matching this
  theme's own existing convention (see the `_`-prefixed line-item property
  skip in sections/main-cart-items.liquid and snippets/cart-drawer.liquid)
  for values not meant to surface to the customer. Neither this theme's
  cart-drawer, cart page, nor checkout ever loops/displays cart.attributes,
  so none of this is customer-visible; it only ever appears in Shopify
  Admin under the order's "Additional details".

  Every cart write first reads the CURRENT /cart.js attributes and merges
  into them (never replaces the whole object), so Shopify's own
  _agentClientInfo attribute and anything else already on the cart is
  always preserved untouched.

  2026-10-14 (DEV): creator-code auto-apply (owner rule: commission follows LAST TOUCH). When this browser holds a valid
  attribution record (same 180-day record as above) and its LAST-TOUCH creator
  has an existing discount code (window.gaCreatorDiscounts, rendered by
  layout/theme.liquid from each creator page's creator.discount_code
  metafield), that code is put on the cart with Shopify's Ajax Cart API
  (POST /cart/update.js { discount }) once the cart holds an eligible product.
  It never replaces a code the shopper (or anyone else) applied, never touches
  attribution, and does nothing for visitors without a creator record.

  No PII is collected — only which of the three known creator pages was
  visited, when, the page path, and any utm_source/utm_medium/utm_campaign
  already present in the URL's own query string.
*/
(function () {
  'use strict';

  if (window.__gaCreatorAttributionLoaded) return;
  window.__gaCreatorAttributionLoaded = true;

  var GA_STORAGE_KEY = 'ga_creator_attribution_v1';
  var GA_WINDOW_MS = 180 * 24 * 60 * 60 * 1000; // 180 days, fixed from first touch
  var GA_SYNC_COOLDOWN_MS = 500;

  var gaSyncInFlight = false;
  var gaOriginalFetchRef = window.fetch.bind(window);
  var gaLastSyncAttemptAt = 0;

  function gaPrivacyAllowed() {
    try {
      if (
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function'
      ) {
        return window.Shopify.customerPrivacy.analyticsProcessingAllowed() !== false;
      }
    } catch (e) {
      /* no-op: if the privacy API throws, don't let that break the page */
    }
    return true;
  }

  function gaFormatDate(iso) {
    try {
      return new Date(iso).toISOString().slice(0, 19).replace('T', ' ') + ' UTC';
    } catch (e) {
      return iso;
    }
  }

  function gaLoadAttribution() {
    try {
      var raw = window.localStorage.getItem(GA_STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || data.v !== 1 || !data.expires || !data.firstTouch || !data.lastTouch) return null;
      if (Date.now() > data.expires) {
        window.localStorage.removeItem(GA_STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function gaSaveAttribution(data) {
    try {
      window.localStorage.setItem(GA_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      /* localStorage unavailable (private mode, quota, etc.) -- fail silently */
    }
  }

  function gaReadUtmParams() {
    var out = { source: '', medium: '', campaign: '' };
    try {
      var params = new URLSearchParams(window.location.search);
      out.source = params.get('utm_source') || '';
      out.medium = params.get('utm_medium') || '';
      out.campaign = params.get('utm_campaign') || '';
    } catch (e) {
      /* no-op */
    }
    return out;
  }

  // Called only on the three known creator pages (window.gaCreatorPageInfo
  // is set inline by layout/theme.liquid, scoped exactly like the
  // existing noindex/flag allowlists elsewhere in this theme).
  function gaRecordCreatorVisit(pageInfo) {
    if (!pageInfo || !pageInfo.trackingName) return;
    if (!gaPrivacyAllowed()) return;

    var data = gaLoadAttribution();
    var utm = gaReadUtmParams();
    // Only carry earlier UTMs forward when the visit is to the SAME creator; a new
    // creator's last touch must never inherit another creator's referral source.
    var prevLast = data && data.lastTouch && data.lastTouch.trackingName === pageInfo.trackingName ? data.lastTouch : null;

    var touch = {
      trackingName: pageInfo.trackingName,
      displayName: pageInfo.displayName,
      page: pageInfo.page,
      dateIso: new Date().toISOString(),
      // A repeat visit with no fresh utm params in the URL keeps the last
      // known value rather than overwriting real UTM data with blanks.
      utmSource: utm.source || (prevLast && prevLast.utmSource) || '',
      utmMedium: utm.medium || (prevLast && prevLast.utmMedium) || '',
      utmCampaign: utm.campaign || (prevLast && prevLast.utmCampaign) || ''
    };

    if (!data) {
      data = {
        v: 1,
        expires: Date.now() + GA_WINDOW_MS,
        firstTouch: touch,
        lastTouch: touch
      };
    } else {
      // Rule 1/2: first touch is never modified after creation; only last touch updates.
      data.lastTouch = touch;
    }
    gaSaveAttribution(data);
  }

  function gaReadAutoCode() {
    try { return window.localStorage.getItem('ga_creator_auto_code_v1') || ''; } catch (e) { return ''; }
  }

  // The auto-applied code as the CART actually holds it: the stored auto code if it is
  // on the cart, else an earlier code this script applied that is on the cart (e.g. a
  // late Tabarnapp write restored it), else blank. So the attribute can never claim a
  // code the cart/order does not carry, and a restored code is visible in the order.
  function gaTruthfulAutoCode(cart) {
    var codes = gaCartCodes(cart);
    var stored = gaReadAutoCode();
    if (stored && codes.indexOf(stored.toLowerCase()) !== -1) return stored;
    var ours = gaAutoHistory();
    var raw = ((cart && cart.discount_codes) || []).map(function (d) { return String(d.code || ''); });
    for (var i = 0; i < raw.length; i++) {
      if (ours.indexOf(raw[i].toLowerCase()) !== -1) return raw[i];
    }
    return '';
  }

  function gaBuildCartAttributes(data, cart) {
    var ft = data.firstTouch;
    var lt = data.lastTouch;
    return {
      'Creator – First Touch': ft.displayName,
      'Creator – Last Touch': lt.displayName,
      'Creator – First Landing Page': ft.page,
      'Creator – Last Landing Page': lt.page,
      'Creator – First Visit': gaFormatDate(ft.dateIso),
      'Creator – Last Visit': gaFormatDate(lt.dateIso),
      _ga_creator_first_utm_source: ft.utmSource || '',
      _ga_creator_first_utm_medium: ft.utmMedium || '',
      _ga_creator_first_utm_campaign: ft.utmCampaign || '',
      _ga_creator_last_utm_source: lt.utmSource || '',
      _ga_creator_last_utm_medium: lt.utmMedium || '',
      _ga_creator_last_utm_campaign: lt.utmCampaign || '',
      // Code this theme applied automatically AND that is on the cart (blank if none) --
      // written by the same sync as everything else so it can never go stale.
      // After a "standard checkout" fallback nothing is claimed (the theme cannot see checkout).
      'Creator – Auto-Applied Code': gaDiscountFrozen ? '' : gaTruthfulAutoCode(cart)
    };
  }

  function gaSyncCartAttribution() {
    var now = Date.now();
    if (gaSyncInFlight) return;
    if (now - gaLastSyncAttemptAt < GA_SYNC_COOLDOWN_MS) return;
    if (!gaPrivacyAllowed()) return;

    var data = gaLoadAttribution();
    if (!data) return;

    gaLastSyncAttemptAt = now;
    gaSyncInFlight = true;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var existing = (cart && cart.attributes) || {};
        var next = gaBuildCartAttributes(data, cart);

        var changed = false;
        for (var key in next) {
          if (existing[key] !== next[key]) {
            changed = true;
            break;
          }
        }
        if (!changed) return null;

        // Merge onto the EXISTING attributes object -- never replace it --
        // so Shopify's own _agentClientInfo (and anything else already on
        // the cart) is always preserved untouched.
        var merged = {};
        for (var k1 in existing) merged[k1] = existing[k1];
        for (var k2 in next) merged[k2] = next[k2];

        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: merged })
        });
      })
      .catch(function () {
        /* never surface a tracking failure to the shopper */
      })
      .finally(function () {
        gaSyncInFlight = false;
      });
  }

  // ---- Creator-code auto-apply (see header). ----
  var GA_AUTO_KEY = 'ga_creator_auto_code_v1';
  // Both existing creator codes are product-scoped to the Electric Grater.
  var GA_ELIGIBLE_HANDLES = ['grandma-anns-electric-grater'];
  var gaDiscountInFlight = false;

  function gaDesiredCreatorCode(data) {
    var map = window.gaCreatorDiscounts || {};
    var touch = data && data.lastTouch;
    return (touch && map[touch.trackingName]) || '';
  }

  // 2026-10-15 creator-code sync fix. On a creator switch, other cart writers (the
  // Tabarnapp app's own /cart/update.js, plus this script's attribute sync) can overlap
  // the discount write; the Ajax Cart API is last-write-wins, so ~1 in 7 switches the
  // cart fell back to the previous creator's code, and because GA_AUTO_KEY had already
  // been set to the new code the fallback then looked like a shopper code. Now:
  //  - every code THIS script successfully wrote is remembered (short history), so a
  //    fallback to one of our own earlier codes is still recognised as ours;
  //  - the discount write waits for this script's own attribute sync to finish;
  //  - GA_AUTO_KEY (and so the 'Creator – Auto-Applied Code' attribute) is set only
  //    after /cart.js confirms the code is really on the cart; if it was lost, retry
  //    (at most GA_MAX_DISCOUNT_WRITES writes per page view, so no endless loop).
  // A shopper/other code is still never replaced, exactly as before.
  var GA_AUTO_HISTORY_KEY = 'ga_creator_auto_history_v1';
  var GA_VERIFY_DELAY_MS = 1200;
  var GA_LATE_CHECK_MS = 4000;
  var GA_MAX_DISCOUNT_WRITES = 4;
  var gaDiscountWrites = 0;
  var gaLastDiscountWriteAt = 0;

  function gaAutoHistory() {
    var h = [];
    try { h = JSON.parse(window.localStorage.getItem(GA_AUTO_HISTORY_KEY) || '[]') || []; } catch (e) { h = []; }
    // Codes applied before this fix were tracked only in GA_AUTO_KEY.
    var legacy = gaReadAutoCode().toLowerCase();
    if (legacy && h.indexOf(legacy) === -1) h.push(legacy);
    return h;
  }

  function gaRememberAutoCode(code) {
    try {
      var lower = code.toLowerCase();
      var h = gaAutoHistory().filter(function (c) { return c !== lower; });
      h.push(lower);
      window.localStorage.setItem(GA_AUTO_HISTORY_KEY, JSON.stringify(h.slice(-5)));
    } catch (e) {}
  }

  // Shopper-relevant codes on the cart, lower-cased, ignoring Tabarnapp's
  // non-applicable "TBN_REMOVE_DISCOUNT_CODES" placeholder (not a shopper's code).
  function gaCartCodes(cart) {
    return ((cart && cart.discount_codes) || [])
      .filter(function (d) { return !(d.applicable === false && /^TBN_/i.test(String(d.code || ''))); })
      .map(function (d) { return String(d.code || '').toLowerCase(); });
  }

  function gaWhenSyncIdle() {
    return new Promise(function (resolve) {
      (function check(tries) {
        if (!gaSyncInFlight || tries >= 20) { resolve(); return; }
        setTimeout(function () { check(tries + 1); }, 250);
      })(0);
    });
  }

  // Record the verified auto code and write it to the cart attribute straight away
  // (/cart/update.js merges attribute keys, so only this one key is sent). Later
  // attribute syncs read the same stored value, so they can never undo it.
  function gaConfirmAutoCode(code) {
    try { window.localStorage.setItem(GA_AUTO_KEY, code); } catch (e) {}
    gaRememberAutoCode(code);
    gaOriginalFetchRef('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Creator – Auto-Applied Code': code } })
    }).catch(function () {});
    document.dispatchEvent(new CustomEvent('ga:creator-discount-applied', { detail: { code: code } }));
  }

  var gaDiscountFrozen = false; // set once the shopper chose "Continue" after a timeout
  function gaEnsureCreatorDiscount() {
    if (gaDiscountInFlight || gaDiscountFrozen || !gaPrivacyAllowed()) return;
    var data = gaLoadAttribution();
    var code = gaDesiredCreatorCode(data);
    if (!code) return;
    var want = code.toLowerCase();
    var retry = false;

    gaDiscountInFlight = true;
    gaOriginalFetchRef('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return null;
        var eligible = cart.items.some(function (item) { return GA_ELIGIBLE_HANDLES.indexOf(item.handle) !== -1; });
        if (!eligible) return null;

        var current = gaCartCodes(cart);
        var ours = gaAutoHistory();
        if (current.indexOf(want) !== -1) {
          // Already on the cart. If THIS script put it there but the record is stale
          // (e.g. a late write restored it), fix the record; never claim a shopper's code.
          if (ours.indexOf(want) !== -1 && gaReadAutoCode().toLowerCase() !== want) gaConfirmAutoCode(code);
          return null;
        }

        // Only an empty cart-code list, or a single code THIS script applied earlier
        // (e.g. the shopper later entered through another creator's link), may be replaced.
        var onlyOurs = current.length === 1 && ours.indexOf(current[0]) !== -1;
        if (current.length && !onlyOurs) return null;
        if (gaDiscountWrites >= GA_MAX_DISCOUNT_WRITES) return null;

        return gaWhenSyncIdle()
          .then(function () {
            gaDiscountWrites += 1;
            // Remember it as ours as soon as it is sent: if the shopper leaves the page
            // before the response, the next page must still recognise it (it is only ever
            // the last-touch creator's own code, never a shopper-typed one).
            gaRememberAutoCode(code);
            gaLastDiscountWriteAt = Date.now();
            try { window.sessionStorage.setItem('ga_creator_last_write', String(gaLastDiscountWriteAt)); } catch (e) {}
            return gaOriginalFetchRef('/cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ discount: code })
            });
          })
          .then(function (res) {
            if (!res.ok) return null;
            // Verify once concurrent cart writers have settled, before recording it.
            return new Promise(function (resolve) { setTimeout(resolve, GA_VERIFY_DELAY_MS); })
              .then(function () { return gaOriginalFetchRef('/cart.js', { credentials: 'same-origin' }); })
              .then(function (r) { return r.json(); })
              .then(function (after) {
                if (gaCartCodes(after).indexOf(want) !== -1) {
                  gaConfirmAutoCode(code);
                  // Late check: a slower concurrent write can still land after this verify.
                  // Re-sync the (truthful) attribute and re-run this check once; it only
                  // writes again if our code was lost, and stays under the write cap.
                  setTimeout(function () {
                    gaLastSyncAttemptAt = 0;
                    gaSyncCartAttribution();
                    gaEnsureCreatorDiscount();
                  }, GA_LATE_CHECK_MS);
                  return;
                }
                retry = gaDiscountWrites < GA_MAX_DISCOUNT_WRITES; // lost to a concurrent write
                if (!retry) { gaLastSyncAttemptAt = 0; gaSyncCartAttribution(); } // give up: record the truth
              });
          });
      })
      .catch(function () { /* never surface to the shopper */ })
      .finally(function () {
        gaDiscountInFlight = false;
        if (retry) setTimeout(gaEnsureCreatorDiscount, 400);
      });
  }

  // ---- 1. Record a creator-page visit, if this is one. ----
  if (window.gaCreatorPageInfo) {
    gaRecordCreatorVisit(window.gaCreatorPageInfo);
  }
  // Buy-it-now safeguard (see layout/theme.liquid): also set after a first creator visit.
  if (gaDesiredCreatorCode(gaLoadAttribution())) {
    document.documentElement.classList.add('ga-creator-referral');
  }

  // ---- 2. Sync immediately on load (covers the full cart page and any
  //         plain returning visit that lands somewhere a cart already
  //         exists, without waiting for a later trigger). ----
  gaSyncCartAttribution();
  gaEnsureCreatorDiscount();

  // ---- 3. Cart icon click -> covers the cart drawer (and cart-notification
  //         popup) opening, on every page. Adding a second listener on
  //         #cart-icon-bubble is safe and standard -- it does not replace
  //         or interfere with Dawn's own CartDrawer listener on the same
  //         element. ----
  document.addEventListener('click', function (evt) {
    var el = evt.target && evt.target.closest && evt.target.closest('#cart-icon-bubble');
    if (el) { gaSyncCartAttribution(); gaEnsureCreatorDiscount(); }
  });

  // ---- 4. Any cart-mutation request (Add to Cart, quantity change, etc.,
  //         from product-form.js, quick-add.js, cart.js, cart-drawer.js --
  //         whichever component made it) -> re-sync once it resolves.
  //         Wrapping fetch only observes calls; it never changes the
  //         request, the response, or timing, so variants, quantity,
  //         Add to Cart and cart updates are all unaffected. The
  //         gaSyncInFlight guard skips this theme script's OWN /cart.js
  //         and /cart/update.js calls, preventing any re-entrant loop. ----
  var gaOriginalFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = '';
    try {
      url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    } catch (e) {
      /* no-op */
    }
    var isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
    var promise = gaOriginalFetch.apply(this, args);
    if (isCartMutation && !gaSyncInFlight) {
      promise.then(function () { gaSyncCartAttribution(); }).catch(function () {});
    }
    if (isCartMutation && !gaDiscountInFlight) {
      promise.then(function () { gaEnsureCreatorDiscount(); }).catch(function () {});
    }
    return promise;
  };

  // ---- 5. Immediately before checkout: fire-and-forget only, never
  //         preventDefault/await, so the native form submit or link
  //         navigation is never delayed. Covers both the full cart page
  //         and cart-drawer checkout buttons (both are real <button
  //         type="submit" name="checkout"> tied to a <form
  //         action="{{ routes.cart_url }}">). Accelerated checkout (Shop
  //         Pay, etc.) is Shopify's own injected widget and is
  //         intentionally left untouched -- by the time a shopper reaches
  //         any checkout method, attributes are already kept in sync by
  //         triggers 1-4 above. ----
  document.addEventListener(
    'submit',
    function (evt) {
      var form = evt.target;
      if (form && form.action && /\/cart(\?|$)/.test(form.action)) gaSyncCartAttribution();
    },
    true
  );
  document.addEventListener(
    'click',
    function (evt) {
      var link = evt.target && evt.target.closest && evt.target.closest('a[href*="/checkout"]');
      if (link) gaSyncCartAttribution();
    },
    true
  );

  // ---- 6. Checkout safeguard (2026-10-16). Only for visitors with a creator referral
  //         (a last-touch creator that has a code). When such a visitor starts checkout
  //         (cart page "Check out", the cart pop-up "Check out", or any /checkout link),
  //         checkout is held until /cart.js shows (a) the last-touch creator's code -- or a
  //         code the shopper entered, which is never replaced -- and (b) the matching
  //         'Creator – Last Touch' / 'Creator – Auto-Applied Code' attributes, and the cart
  //         has stayed that way briefly after our last discount write. In sync -> one cart
  //         read, then checkout continues. Visitors without a creator referral are never
  //         held. If it cannot be confirmed within GA_GATE_TIMEOUT_MS the shopper gets
  //         "Try again" or "Continue to standard checkout" (told to enter the code
  //         themselves); continuing claims no auto code and flags the order
  //         'Creator – Sync Status: UNVERIFIED - standard checkout'. Nothing is ever
  //         labelled verified: the theme cannot see checkout.
  //         Cart-page express buttons (Shop Pay etc.) cannot be intercepted, so for these
  //         visitors they stay hidden (layout/theme.liquid) until a check passes. ----
  var GA_GATE_TIMEOUT_MS = 6000;
  var GA_GATE_SETTLE_MS = 3000; // no Tabarnapp/other overwrite for this long after our write
  var GA_ES = /^es/i.test(document.documentElement.lang || '');
  var GA_TXT = GA_ES ? {
    wait: 'Confirmando tu descuento de creador…',
    fail: 'No pudimos confirmar tu descuento de creador.',
    failMore: 'Intenta de nuevo o continúa con el pago estándar. Es posible que tu descuento de creador no se aplique automáticamente: si no ves el código {code} en el pago, escríbelo en la casilla de descuento.',
    retry: 'Intentar de nuevo',
    go: 'Continuar al pago estándar',
    close: 'Cerrar'
  } : {
    wait: 'Confirming your creator discount…',
    fail: 'We couldn’t confirm your creator discount.',
    failMore: 'Try again, or continue with standard checkout. Your creator discount may not be applied automatically: if you don’t see code {code} at checkout, enter it in the discount box.',
    retry: 'Try again',
    go: 'Continue to standard checkout',
    close: 'Close'
  };
  var gaGateRunning = false;

  // Only when tracking is allowed (otherwise this script never syncs or auto-applies,
  // so there is nothing to wait for and checkout must not be held).
  function gaHasReferral() { return gaPrivacyAllowed() && !!gaDesiredCreatorCode(gaLoadAttribution()); }

  // Last discount write time, shared across page loads in this tab, so a write made on
  // the creator page still gets its settle window if the shopper moves on quickly.
  function gaLastWriteAt() {
    var s = 0;
    try { s = parseInt(window.sessionStorage.getItem('ga_creator_last_write') || '0', 10) || 0; } catch (e) {}
    return Math.max(s, gaLastDiscountWriteAt);
  }

  // Why the cart is not ready yet ([] = ready).
  function gaGateReasons(cart, data) {
    var reasons = [];
    var code = gaDesiredCreatorCode(data);
    if (!code || !cart) return reasons;
    var eligible = (cart.items || []).some(function (item) { return GA_ELIGIBLE_HANDLES.indexOf(item.handle) !== -1; });
    var codes = gaCartCodes(cart);
    if (eligible && codes.indexOf(code.toLowerCase()) === -1) {
      var ours = gaAutoHistory();
      // A shopper/other code is kept (never replaced) -> not a reason to hold.
      var replaceable = !codes.length || (codes.length === 1 && ours.indexOf(codes[0]) !== -1);
      if (replaceable) reasons.push('code');
    }
    var attrs = cart.attributes || {};
    if (attrs['Creator – Last Touch'] !== data.lastTouch.displayName) reasons.push('last-touch');
    if ((attrs['Creator – Auto-Applied Code'] || '') !== gaTruthfulAutoCode(cart)) reasons.push('auto-code');
    return reasons;
  }

  function gaReadCart() {
    return gaOriginalFetchRef('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(function (r) { return r.json(); });
  }
  function gaSleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  // Resolves {ok:true} when ready, {ok:false, reasons} on timeout.
  function gaCheckoutReady() {
    var started = Date.now();
    var acted = false;
    function attempt() {
      var data = gaLoadAttribution();
      if (!gaDesiredCreatorCode(data)) return Promise.resolve({ ok: true });
      return gaReadCart().then(function (cart) {
        var reasons = gaGateReasons(cart, data);
        var busy = gaDiscountInFlight || gaSyncInFlight;
        var lastWrite = gaLastWriteAt();
        var settling = lastWrite && Date.now() - lastWrite < GA_GATE_SETTLE_MS;
        if (!reasons.length && !busy && !settling) return { ok: true };
        if (Date.now() - started > GA_GATE_TIMEOUT_MS) return { ok: false, reasons: reasons.length ? reasons : ['busy'] };
        if (reasons.indexOf('code') !== -1 && !gaDiscountInFlight) { acted = true; gaEnsureCreatorDiscount(); }
        else if ((reasons.indexOf('last-touch') !== -1 || reasons.indexOf('auto-code') !== -1) && !gaSyncInFlight) {
          acted = true; gaLastSyncAttemptAt = 0; gaSyncCartAttribution();
        }
        return gaSleep(acted ? 350 : 200).then(attempt);
      }).catch(function () {
        if (Date.now() - started > GA_GATE_TIMEOUT_MS) return { ok: false, reasons: ['network'] };
        return gaSleep(400).then(attempt);
      });
    }
    return attempt();
  }

  var gaStatusEl = null;
  function gaShowStatus(show) {
    if (!gaStatusEl) {
      gaStatusEl = document.createElement('div');
      gaStatusEl.className = 'ga-sync-status';
      gaStatusEl.setAttribute('role', 'status');
      gaStatusEl.setAttribute('aria-live', 'polite');
      gaStatusEl.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483000;' +
        'background:#0D4A37;color:#F7F3E9;padding:12px 18px;border-radius:6px;font:inherit;font-size:1.5rem;' +
        'box-shadow:0 4px 16px rgba(0,0,0,.25);max-width:calc(100vw - 32px);text-align:center';
      document.body.appendChild(gaStatusEl);
    }
    gaStatusEl.textContent = show ? GA_TXT.wait : '';
    gaStatusEl.style.display = show ? 'block' : 'none';
  }

  function gaShowTimeout(onRetry, onGo, opener) {
    var overlay = document.createElement('div');
    overlay.className = 'ga-sync-dialog';
    overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483001;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:16px';
    var box = document.createElement('div');
    box.setAttribute('role', 'alertdialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'ga-sync-title');
    box.setAttribute('aria-describedby', 'ga-sync-desc');
    box.style.cssText = 'background:#F7F3E9;color:#151A18;max-width:420px;width:100%;padding:24px;border-radius:8px;font:inherit';
    box.innerHTML = '<h2 id="ga-sync-title" style="margin:0 0 8px;font-size:1.8rem"></h2><p id="ga-sync-desc" style="margin:0 0 16px"></p>' +
      '<div style="display:flex;gap:8px;flex-wrap:wrap"><button type="button" class="button" data-ga-retry></button>' +
      '<button type="button" class="button button--secondary" data-ga-go></button></div>';
    box.querySelector('#ga-sync-title').textContent = GA_TXT.fail;
    box.querySelector('#ga-sync-desc').textContent = GA_TXT.failMore.replace('{code}', String(gaDesiredCreatorCode(gaLoadAttribution()) || '').toUpperCase());
    var retryBtn = box.querySelector('[data-ga-retry]'); retryBtn.textContent = GA_TXT.retry;
    var goBtn = box.querySelector('[data-ga-go]'); goBtn.textContent = GA_TXT.go;
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    function close() { overlay.remove(); document.removeEventListener('keydown', onKey, true); if (opener && opener.focus) opener.focus(); }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (e.key === 'Tab') { // keep focus inside the two buttons
        var first = retryBtn, last = goBtn;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', onKey, true);
    retryBtn.addEventListener('click', function () { close(); onRetry(); });
    goBtn.addEventListener('click', function () { close(); onGo(); });
    retryBtn.focus();
  }

  // Fallback ("standard checkout"): stop this script's discount writes, make no discount
  // write of its own, and claim nothing -- the Auto-Applied attribute is blanked and the
  // order is flagged, because the theme cannot see what checkout finally applies. The
  // cart's codes (incl. any shopper code) are left exactly as they are.
  function gaMarkUnverified(reasons) {
    gaDiscountFrozen = true;
    var data = gaLoadAttribution();
    function waitIdle(n) {
      if ((!gaDiscountInFlight && !gaSyncInFlight) || n <= 0) return Promise.resolve();
      return gaSleep(100).then(function () { return waitIdle(n - 1); });
    }
    var attrs = { 'Creator – Auto-Applied Code': '', 'Creator – Sync Status': 'UNVERIFIED - standard checkout (' + reasons.join(', ') + ')' };
    if (data) attrs['Creator – Last Touch'] = data.lastTouch.displayName;
    return waitIdle(20).then(function () {
      return gaOriginalFetchRef('/cart/update.js', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      });
    }).catch(function () {});
  }

  function gaRunGate(proceed, opener) {
    if (gaGateRunning) return;
    gaGateRunning = true;
    var t = setTimeout(function () { gaShowStatus(true); }, 150); // no flash for the instant (in-sync) case
    if (opener) opener.setAttribute('aria-busy', 'true');
    gaCheckoutReady().then(function (res) {
      clearTimeout(t); gaShowStatus(false); gaGateRunning = false;
      if (opener) opener.removeAttribute('aria-busy');
      if (res.ok) { document.documentElement.classList.add('ga-creator-ready'); proceed(); return; }
      gaShowTimeout(function () { gaRunGate(proceed, opener); },
        function () { gaMarkUnverified(res.reasons).then(proceed); }, opener);
    });
  }

  function gaContinueForm(form, submitter) {
    // Native submit() skips submit listeners; re-add the "checkout" name the button carried.
    var name = submitter && submitter.getAttribute('name');
    if (name) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden'; hidden.name = name; hidden.value = submitter.value || '1';
      form.appendChild(hidden);
    }
    HTMLFormElement.prototype.submit.call(form);
  }

  // The installed Tabarnapp app hooks checkout buttons' own CLICK (it blocks the native
  // submit, writes its __tbn_token attribute, then continues to checkout itself), so a
  // submit listener alone is never reached on those buttons. Catch the click first
  // (window capture runs before the button's own listeners), hold it, then replay the
  // same click once verified so Tabarnapp and the normal checkout run exactly as before.
  var gaGatePassedAt = 0;
  window.addEventListener('click', function (evt) {
    var btn = evt.target && evt.target.closest &&
      evt.target.closest('button[name="checkout"], input[name="checkout"], #checkout');
    if (!btn || btn.disabled || !gaHasReferral()) return;
    if (btn.__gaGatePass) { btn.__gaGatePass = false; gaGatePassedAt = Date.now(); return; }
    evt.preventDefault();
    evt.stopImmediatePropagation();
    gaRunGate(function () { btn.__gaGatePass = true; btn.click(); }, btn);
  }, true);

  // Backup for checkout submits that arrive without a button click (none in this theme
  // today). Skipped right after a gated click, so a shopper is never held twice.
  window.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form || !form.action || !/\/cart(\?|$)/.test(form.action)) return;
    var submitter = evt.submitter || null;
    var isCheckout = (submitter && submitter.getAttribute('name') === 'checkout') || form.id === 'cart-notification-form';
    if (!isCheckout || !gaHasReferral() || Date.now() - gaGatePassedAt < 5000) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    gaRunGate(function () { gaGatePassedAt = Date.now(); gaContinueForm(form, submitter); }, submitter);
  }, true);

  window.addEventListener('click', function (evt) {
    var link = evt.target && evt.target.closest && evt.target.closest('a[href*="/checkout"]');
    if (!link || !gaHasReferral()) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    gaRunGate(function () { window.location.href = link.href; }, link);
  }, true);

  // Cart-page express buttons (Shop Pay etc.) are Shopify's own widget and cannot be
  // held, so for referral visitors they stay hidden until a check passes. The rule is
  // injected here (this script runs before DOMContentLoaded, ahead of Shopify's async
  // accelerated-checkout render) and only for referral visitors.
  if (gaHasReferral()) {
    var gaGuardCss = document.createElement('style');
    gaGuardCss.textContent = 'html.ga-creator-referral:not(.ga-creator-ready) .cart__dynamic-checkout-buttons,' +
      'html.ga-creator-referral:not(.ga-creator-ready) .additional-checkout-buttons{visibility:hidden!important}';
    document.head.appendChild(gaGuardCss);
  }
  // Reveal them once a check passes (cart page only, so no extra cart reads elsewhere).
  if (gaHasReferral() && /\/cart\/?$/.test(window.location.pathname)) {
    gaCheckoutReady().then(function (res) { if (res.ok) document.documentElement.classList.add('ga-creator-ready'); });
    document.addEventListener('ga:creator-discount-applied', function () {
      gaCheckoutReady().then(function (res) { if (res.ok) document.documentElement.classList.add('ga-creator-ready'); });
    });
  }
})();
